import * as React from "react"

import {
  getStorageItem,
  setStorageItem,
} from "@exxatdesignux/ui/lib/persisted-state"

import type { NotificationKind } from "@/lib/mock/notifications"

export interface NotificationPreferences {
  kinds: Record<NotificationKind, boolean>
}

const STORAGE_KEY = "notification-prefs:v1"

const DEFAULT_PREFS: NotificationPreferences = {
  kinds: {
    import: true,
    assessment: true,
    submission: true,
  },
}

const KIND_KEYS: readonly NotificationKind[] = [
  "import",
  "assessment",
  "submission",
]

function parsePrefs(raw: string | null): NotificationPreferences {
  if (!raw) return DEFAULT_PREFS
  try {
    const parsed = JSON.parse(raw) as Partial<NotificationPreferences> & {
      toasts?: boolean
    }
    const kinds = { ...DEFAULT_PREFS.kinds }
    for (const kind of KIND_KEYS) {
      if (typeof parsed.kinds?.[kind] === "boolean") {
        kinds[kind] = parsed.kinds[kind]
      }
    }
    return { kinds }
  } catch {
    return DEFAULT_PREFS
  }
}

let prefs: NotificationPreferences = parsePrefs(getStorageItem(STORAGE_KEY))
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

function persist() {
  setStorageItem(STORAGE_KEY, JSON.stringify(prefs))
  emit()
}

export function getNotificationPreferences(): NotificationPreferences {
  return prefs
}

export function subscribeNotificationPreferences(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => {
    listeners.delete(onChange)
  }
}

export function isNotificationKindEnabled(kind: NotificationKind): boolean {
  return prefs.kinds[kind]
}

export function setNotificationKindEnabled(
  kind: NotificationKind,
  enabled: boolean
) {
  if (prefs.kinds[kind] === enabled) return
  prefs = {
    ...prefs,
    kinds: { ...prefs.kinds, [kind]: enabled },
  }
  persist()
}

export function resetNotificationPreferencesForTests(
  next: NotificationPreferences = DEFAULT_PREFS
) {
  prefs = {
    kinds: { ...next.kinds },
  }
  emit()
}

export function useNotificationPreferences(): NotificationPreferences {
  return React.useSyncExternalStore(
    subscribeNotificationPreferences,
    getNotificationPreferences,
    getNotificationPreferences
  )
}
